import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Search, X } from "lucide-react";
import useModalDismiss from "../../common/useModalDismiss";

/* The full ranked list, for when a panel has more rows than it shows.

   A shop with ten staff and forty services turned every breakdown card into a
   column that scrolled past the fold, and three of them sit side by side — so
   the page grew with the business until it was unusable at the size that
   mattered most. The cards now stop at a readable number of rows and hand the
   rest to this dialog, which is the one place a long list is fine: it has its
   own scroll, its own search, and it closes.

   Read-only, so a backdrop click is safe to dismiss on. */

const SEARCHABLE_FROM = 10;

const RowsModal = ({ title, rows, total, renderRow, nameOf, onClose, t }) => {
	const dismissProps = useModalDismiss(onClose, { dismissOnBackdrop: true });
	const [query, setQuery] = useState("");

	/* The original position travels with the row, so a search that hides the
	   rows above does not renumber the ones below it — a filtered #7 is still
	   the seventh best, not the first result. */
	const filtered = useMemo(() => {
		const ranked = rows.map((row, rank) => ({ row, rank }));
		const q = query.trim().toLowerCase();
		if (!q) return ranked;
		return ranked.filter(({ row }) => String(nameOf(row) || "").toLowerCase().includes(q));
	}, [rows, query, nameOf]);

	const searchable = rows.length >= SEARCHABLE_FROM;

	return createPortal(
		<div className="sf-rap-modal-backdrop" {...dismissProps}>
			<div className="sf-rap-modal sf-rap-modal--list" role="dialog" aria-modal="true" aria-label={title}>
				<header className="sf-rap-modal-head">
					<div className="sf-rap-modal-head-copy">
						<h2>{title}</h2>
						<span>{total}</span>
					</div>
					<button type="button" className="sf-rap-icon-btn" onClick={onClose} aria-label={t.breakdown.close}>
						<X size={18} />
					</button>
				</header>

				{searchable && (
					<div className="sf-rap-modal-tools">
						<label className="sf-rap-search">
							<Search size={16} />
							<input
								type="text"
								value={query}
								placeholder={t.breakdown.searchRows}
								onChange={(e) => setQuery(e.target.value)}
							/>
						</label>
					</div>
				)}

				<div className="sf-rap-modal-body">
					{filtered.length === 0 ? (
						<p className="sf-rap-empty">{t.breakdown.noMatches}</p>
					) : (
						<ol className="sf-rap-rank-list">
							{filtered.map(({ row, rank }) => renderRow(row, rank))}
						</ol>
					)}
				</div>
			</div>
		</div>,
		document.body,
	);
};

export default RowsModal;
